import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {toast} from "sonner";
import {
  listSavedAiPersonas,
  updateAiPersona,
  deleteAiPersona,
  type AiPersonaResponse,
  type AiPersonaUpdate,
  type AiPersona,
  AiPersonaGenerationRequest,
  fetchLocations,
  Platform,
  fetchPlatforms,
  Location,
  AiPersonaGenerationResponse,
  generatePersonas,
} from "@/lib/personas-api";

const AI_PERSONAS_KEY = "ai-personas";
// MAX_PROFILES hardcoded to 4 - UI control intentionally removed for simplified UX
const MAX_PROFILES = 4;

/**
 * Hook to fetch available locations for filtering
 */
export function useLocations() {
  return useQuery<Location[], Error>({
    queryKey: ["locations"],
    queryFn: fetchLocations,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch available social media platforms
 */
export function usePlatforms() {
  return useQuery<Platform[], Error>({
    queryKey: ["platforms"],
    queryFn: fetchPlatforms,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
}

/**
 * Hook to generate AI personas from mentions
 */
export function useGeneratePersonas() {
  return useMutation<AiPersonaGenerationResponse, Error, AiPersonaGenerationRequest>({
    mutationFn: generatePersonas,
  });
}

/**
 * Filter state type for personas generation
 */
export interface PersonasFilterState {
  locationId: string | null;
  date: string | null;
  platformIds: string[];
}

/**
 * Default filter state
 */
export const DEFAULT_FILTER_STATE: PersonasFilterState = {
  locationId: null,
  date: "LAST_30_DAYS",
  platformIds: [],
};

/**
 * Convert filter state to API request format
 */
export function filterStateToRequest(filters: PersonasFilterState): AiPersonaGenerationRequest {
  return {
    location_id: filters.locationId || undefined,
    date: filters.date || undefined,
    selected_platform_ids: filters.platformIds.length > 0 ? filters.platformIds : undefined,
    max_profiles: MAX_PROFILES,
  };
}

export function useAiPersonas() {
  return useQuery({
    queryKey: [AI_PERSONAS_KEY],
    queryFn: () => listSavedAiPersonas(),
  });
}

export function useUpdateAiPersona() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({id, data}: {id: string; data: AiPersonaUpdate}) => updateAiPersona(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [AI_PERSONAS_KEY]});
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteAiPersona() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAiPersona(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [AI_PERSONAS_KEY]});
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/**
 * Extract an AiPersona from an AiPersonaResponse's profile_data
 */
export function extractAiPersona(aiPersona: AiPersonaResponse): AiPersona {
  return aiPersona.profile_data as unknown as AiPersona;
}
